import { useEffect, useState } from "react";

import type { AuthClient, MeResponse } from "@nav-games/auth-client";

import { AUTH_BASE_URL } from "../config";
import { LinkedAccounts } from "../ui/LinkedAccounts";

interface Props {
  auth: AuthClient;
  onSignOut: () => void;
}

export function Me({ auth, onSignOut }: Props) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "anonymous" }
    | { kind: "loaded"; me: MeResponse }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    auth
      .me()
      .then((me) => {
        if (cancelled) return;
        setState(me ? { kind: "loaded", me } : { kind: "anonymous" });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [auth]);

  if (state.kind === "loading") {
    return (
      <div className="card">
        <h2>Loading…</h2>
      </div>
    );
  }
  if (state.kind === "anonymous") {
    return (
      <div className="card">
        <h2>Not signed in</h2>
        <p>
          <a href="/">Back to the home page</a> and use the sign-in form.
        </p>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="card">
        <h2>Couldn't load profile</h2>
        <p className="muted">{state.message}</p>
      </div>
    );
  }
  const me = state.me;
  return (
    <>
    <div className="card">
      <h2>Account</h2>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", margin: 0 }}>
        <dt className="muted">User id</dt>
        <dd style={{ margin: 0 }}>
          <code>{me.user.id}</code>
        </dd>
        <dt className="muted">Display name</dt>
        <dd style={{ margin: 0 }}>{me.user.displayName ?? <em className="muted">(none)</em>}</dd>
        {me.user.email && (
          <>
            <dt className="muted">Email</dt>
            <dd style={{ margin: 0 }}>{me.user.email}</dd>
          </>
        )}
        <dt className="muted">Audience</dt>
        <dd style={{ margin: 0 }}>
          <code>{me.audience}</code>
        </dd>
        <dt className="muted">Created</dt>
        <dd style={{ margin: 0 }}>{new Date(me.user.createdAt).toLocaleString()}</dd>
      </dl>
      <h3 style={{ marginTop: 20, marginBottom: 6, fontSize: 15 }}>Entitlements</h3>
      {me.entitlements.length === 0 ? (
        <p className="muted small">No active entitlements.</p>
      ) : (
        <ul className="small">
          {me.entitlements.map((e) => (
            <li key={`${e.sku}-${e.source}`}>
              <code>{e.sku}</code> via <code>{e.source}</code>
              {e.expiresAt && <span className="muted"> — expires {new Date(e.expiresAt).toLocaleString()}</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="row" style={{ marginTop: 20 }}>
        <button
          className="secondary"
          onClick={() => {
            auth.signOut();
            onSignOut();
          }}
        >
          Sign out
        </button>
      </div>
    </div>
    <LinkedAccounts auth={auth} />

    {/* Account deletion — required in-app by App Store 5.1.1(v) and
        Play Console's Data Safety rules. The full delete flow runs on
        auth.goastern.com (shared across log + nav-games + radar); the
        page handles re-authentication, confirmation, and the actual
        cascade-delete. Opening the URL externally also matches what
        Apple explicitly permits ("include a link directly to the
        website page where they can complete the process"). */}
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Delete account</h2>
      <p className="muted small">
        Permanently remove your GoAstern sign-in identity, linked
        sign-in methods, and any purchases tied to it. This affects
        every app that uses GoAstern sign-in (log, nav-games, radar).
      </p>
      <p className="muted small">
        The deletion page is on <code>auth.goastern.com</code> — it
        will sign you in again (so you have to confirm with the same
        email) and walk you through the steps. Opens in your browser.
      </p>
      <div className="row" style={{ marginTop: 8 }}>
        <button
          type="button"
          onClick={() => {
            window.open(
              `${AUTH_BASE_URL}/account/delete`,
              "_blank",
              "noopener,noreferrer",
            );
          }}
          style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
          className="secondary"
        >
          Delete my account →
        </button>
      </div>
    </div>
    </>
  );
}
